import { getMenuServices } from '../../utils/menuServices';
import { getServiceIcon } from "../../utils/serviceIcons";
import { getServiceMeta } from "../../utils/serviceColors";
import i18n from "../../i18n";import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, LayoutDashboard } from "lucide-react";
import { cn } from "../../lib/utils";
import { buildNavTree, cleanName, isPathActive } from "../../router/navigation";
import { useTranslation } from "react-i18next";

function isLegacyDashboardLink(to) {
  return typeof to === "string" && to.startsWith("/dashboard.html");
}

/**
 * Horizontal alternative to Sidebar. It uses the same login-provided
 * enabledServices tree. A service first shows its modules; hovering a module
 * opens its submodule links beside the module list.
 */
export function Horizontalbar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [menuServices, setMenuServices] = useState(getMenuServices);
  useEffect(() => {
    const refresh = () => setMenuServices(getMenuServices());
    window.addEventListener('session-updated', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('session-updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  const navTree = useMemo(() => buildNavTree(menuServices), [menuServices]);
  const [openService, setOpenService] = useState(null);
  const [openModule, setOpenModule] = useState(null);
  const barRef = useRef(null);
  const menuRef = useRef(null);
  const serviceButtonRefs = useRef({});
  const [menuPosition, setMenuPosition] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState(null);
  const closeTimer = useRef(null);
  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      setOpenService(null);
      setOpenModule(null);
    }, 180);
  };
  const showService = (name) => {
    cancelClose();
    if (openService === name) return;
    const rect = serviceButtonRefs.current[name]?.getBoundingClientRect();
    if (!rect) return;
    setMenuPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 272)),
      top: rect.bottom + 8,
      maxHeight: Math.max(0, window.innerHeight - rect.bottom - 16)
    });
    setOpenService(name);
    setOpenModule(null);
  };
  const showModule = (event, module) => {
    cancelClose();
    if (!module.submodules.length) {
      setOpenModule(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const panel = menuRef.current.getBoundingClientRect();
    setSubmenuPosition({
      left: panel.right + 264 > window.innerWidth ? Math.max(8, panel.left - 256) : panel.right,
      top: rect.top,
      maxHeight: Math.max(0, window.innerHeight - rect.top - 8)
    });
    setOpenModule(module.name);
  };

  useEffect(() => {
    const dismiss = () => {
      window.clearTimeout(closeTimer.current);
      setOpenService(null);
      setOpenModule(null);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") dismiss();
    };
    window.addEventListener("resize", dismiss);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(closeTimer.current);
      window.removeEventListener("resize", dismiss);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    setOpenService(null);
    setOpenModule(null);
  }, [pathname]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!barRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setOpenService(null);
        setOpenModule(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  return (
    <nav
      ref={barRef}
      className="hrms-shell__horizontalbar relative z-40 bg-card px-4 sm:px-6"
      aria-label={i18n.t("layout.mainNavigation")}>
      
        <div className="flex min-h-12 flex-nowrap items-center gap-1 overflow-x-auto py-1 scrollbar-none [&::-webkit-scrollbar]:hidden">
        <Link
          to="/"
          className={cn(
            "flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3 text-sm font-medium transition",
            pathname === "/" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-primary"
          )}>
          
          <LayoutDashboard className="h-4 w-4" />
          {t("common.dashboard")}
        </Link>

        {navTree.map((service) => {
          const ServiceIcon = getServiceIcon(service.name);
          const serviceActive = service.modules.some((module) =>
          module.submodules.some((submodule) => isPathActive(pathname, submodule.to))
          );
          const isOpen = openService === service.name;

          return (
            <div key={service.name} className="relative shrink-0">
              <button
                type="button"
                ref={(node) => {
                  serviceButtonRefs.current[service.name] = node;
                }}
                onMouseEnter={() => showService(service.name)}
                onMouseLeave={scheduleClose}
                onFocus={() => showService(service.name)}
                className={cn(
                  "flex h-9 items-center gap-1 whitespace-nowrap rounded-lg px-3 text-sm font-medium transition",
                  serviceActive || isOpen ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-primary"
                )}
                aria-expanded={isOpen}>
                
                <ServiceIcon className="h-[18px] w-[18px] shrink-0" style={{ color: getServiceMeta(service.name).color }} />
                {service.name}
                <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
              </button>

              {isOpen && menuPosition && createPortal(
                <div
                  ref={menuRef}
                  onMouseEnter={cancelClose}
                  onMouseLeave={scheduleClose}
                  className="fixed z-60 w-64"
                  style={{ left: menuPosition.left, top: menuPosition.top }}>
                  <div
                    className="overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
                    style={{ maxHeight: Math.min(menuPosition.maxHeight, 512) }}
                    onScroll={() => setOpenModule(null)}>
                    <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {service.name}
                    </p>
                    <div className="divide-y divide-border">
                      {service.modules.map((module) => (
                        <button
                          key={module.name}
                          type="button"
                          onMouseEnter={(event) => showModule(event, module)}
                          onFocus={(event) => showModule(event, module)}
                          aria-expanded={openModule === module.name}
                          className={cn(
                            "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100",
                            openModule === module.name && "bg-slate-100"
                          )}>
                          {cleanName(module.name)}
                          {module.submodules.length > 0 && <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                        </button>
                      ))}
                    </div>
                  </div>
                  {openModule && submenuPosition && (
                    <div
                      className="fixed w-64 divide-y divide-border overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
                      style={submenuPosition}>
                      {service.modules.find((module) => module.name === openModule)?.submodules.map((submodule) => {
                        const active = isPathActive(pathname, submodule.to);
                        const className = cn(
                          "block px-3 py-2 text-sm transition",
                          active ? "bg-accent text-white" : "text-slate-700 hover:bg-slate-100"
                        );
                        return isLegacyDashboardLink(submodule.to) ? (
                          <a key={submodule.name} href={submodule.to} className={className}>
                            {cleanName(submodule.name)}
                          </a>
                        ) : (
                          <Link key={submodule.name} to={submodule.to} className={className}>
                            {cleanName(submodule.name)}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>,
                document.body)}
            </div>);

        })}

        {navTree.length === 0 &&
        <span className="px-3 text-sm text-white/60">{t("common.noServices")}</span>
        }

        {/* <div className="ml-auto flex h-9 items-center gap-2 pl-3 text-white">
           <div className="flex min-w-0 items-center gap-2 text-sm">
             <UserCircle2 className="h-5 w-5 shrink-0" />
             <span className="hidden max-w-36 truncate lg:block" title={`${emp.first_name || ""} ${emp.last_name || ""}`.trim()}>
               {emp.first_name || emp.last_name ? `${emp.first_name || ""} ${emp.last_name || ""}` : initials}
             </span>
           </div>
           <button
             type="button"
             onClick={handleLogout}
             className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-rose-200 hover:bg-white/10 hover:text-white"
             title="Sign Out"
             aria-label="Sign Out"
           >
             <LogOut className="h-4 w-4" />
           </button>
          </div> */}
      </div>
    </nav>);

}
